import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import {
    Card,
    CardBody,
    Typography,
} from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSyncAlt, faExclamationTriangle, faCalendarAlt, faMoneyBillWave, faTools, faCogs } from '@fortawesome/free-solid-svg-icons';
const { rootUrl } = require('../../../config/config');

const IncomeReport = () => {
    const { token } = useAuth();
    const authToken = token || localStorage.getItem('authToken') || 'default-token';

    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        date_from: '',
        date_to: '',
        reference_type: ''
    });

    const fetchReport = async () => {
        setLoading(true);
        try {
            const params = { ...filters };

            // Remove empty filters
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === undefined) {
                    delete params[key];
                }
            });

            const response = await axios.get(
                `${rootUrl}/api/cashbook/income-report`,
                {
                    headers: { 'Authorization': `Bearer ${authToken}` },
                    params
                }
            );

            if (response.data && response.data.success) {
                setReport(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching income report:', error);
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchReport();
    }, []);

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-KE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount || 0);
    };

    return (
        <div className="space-y-6">
            {/* Filters Row */}
            <div className="flex flex-wrap items-end gap-4 bg-gray-50/50 p-4 rounded-xl border border-gray-100">
                <div className="flex-1 min-w-[200px]">
                    <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1 ml-1 text-center">Filter Period</label>
                    <div className="flex items-center gap-2">
                        <div className="relative flex-1">
                            <input
                                type="date"
                                className="w-full pl-8 pr-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs focus:ring-1 focus:ring-[#FF5733] outline-none"
                                value={filters.date_from}
                                onChange={(e) => setFilters(f => ({ ...f, date_from: e.target.value }))}
                            />
                            <FontAwesomeIcon icon={faCalendarAlt} className="absolute left-2.5 top-2 text-gray-300 text-[10px]" />
                        </div>
                        <span className="text-gray-300 font-bold">~</span>
                        <div className="relative flex-1">
                            <input
                                type="date"
                                className="w-full pl-8 pr-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs focus:ring-1 focus:ring-[#FF5733] outline-none"
                                value={filters.date_to}
                                onChange={(e) => setFilters(f => ({ ...f, date_to: e.target.value }))}
                            />
                            <FontAwesomeIcon icon={faCalendarAlt} className="absolute left-2.5 top-2 text-gray-300 text-[10px]" />
                        </div>
                    </div>
                </div>
                
                <button 
                    onClick={fetchReport}
                    disabled={loading}
                    className="flex items-center gap-2 px-6 py-2 bg-white border border-gray-200 text-gray-600 rounded-lg text-xs font-bold hover:border-[#FF5733] hover:text-[#FF5733] transition-all disabled:opacity-50"
                >
                    <FontAwesomeIcon icon={faSyncAlt} className={loading ? 'spin' : ''} />
                    Generate Report
                </button>
            </div>

            {/* Summary Grid */}
            {report && report.totals && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Card className="rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                        <CardBody className="p-6">
                            <div className="flex justify-between items-start mb-4">
                                <div className="p-2 bg-green-50 rounded-lg text-green-600">
                                    <FontAwesomeIcon icon={faMoneyBillWave} />
                                </div>
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Total Revenue</span>
                            </div>
                            <Typography variant="h3" className="text-green-600 font-black mb-1">
                                KES {formatCurrency(report.totals.total_income)}
                            </Typography>
                            <p className="text-xs text-gray-400 font-medium tracking-tight">Combined earnings from all sources</p>
                        </CardBody>
                    </Card>

                    <Card className="rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                        <CardBody className="p-6">
                            <div className="flex justify-between items-start mb-4">
                                <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                                    <FontAwesomeIcon icon={faTools} />
                                </div>
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Service Charges</span>
                            </div>
                            <Typography variant="h3" className="text-blue-600 font-black mb-1">
                                KES {formatCurrency(report.totals.service_charges)}
                            </Typography>
                            <p className="text-xs text-gray-400 font-medium tracking-tight">Income from labor and service fees</p>
                        </CardBody>
                    </Card>

                    <Card className="rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                        <CardBody className="p-6">
                            <div className="flex justify-between items-start mb-4">
                                <div className="p-2 bg-purple-50 rounded-lg text-purple-600">
                                    <FontAwesomeIcon icon={faCogs} />
                                </div>
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Parts Sales</span>
                            </div>
                            <Typography variant="h3" className="text-purple-600 font-black mb-1">
                                KES {formatCurrency(report.totals.parts_sales)}
                            </Typography>
                            <p className="text-xs text-gray-400 font-medium tracking-tight">Income from hardware and parts</p>
                        </CardBody>
                    </Card>
                </div>
            )}

            {/* Entries Table */}
            {loading ? (
                <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-gray-100">
                    <FontAwesomeIcon icon={faSyncAlt} spin className="text-3xl text-[#FF5733] mb-4" />
                    <span className="text-sm font-medium text-gray-500">Processing report data...</span>
                </div>
            ) : !report || !report.entries || report.entries.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-gray-100 border-dashed">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="text-4xl text-gray-200 mb-4" />
                    <Typography className="text-gray-400 font-bold">No income data for this period</Typography>
                </div>
            ) : (
                <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-50/50">
                                    <th className="px-6 py-4 text-[10px] uppercase font-black text-gray-400 border-b border-gray-100">Date</th>
                                    <th className="px-6 py-4 text-[10px] uppercase font-black text-gray-400 border-b border-gray-100">Source</th>
                                    <th className="px-6 py-4 text-[10px] uppercase font-black text-gray-400 border-b border-gray-100">Description</th>
                                    <th className="px-6 py-4 text-[10px] uppercase font-black text-gray-400 border-b border-gray-100">Reference</th>
                                    <th className="px-6 py-4 text-[10px] uppercase font-black text-gray-400 border-b border-gray-100 text-right">Revenue</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {report.entries.map((entry) => {
                                    const refType = entry.reference_type || 'MANUAL';
                                    return (
                                        <tr key={entry.id} className="hover:bg-gray-25 transition-colors">
                                            <td className="px-6 py-4">
                                                <span className="text-sm font-bold text-gray-700">{formatDate(entry.entry_date)}</span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`px-2 py-1 rounded text-[10px] font-bold tracking-tight ${
                                                    refType === 'JOB_CARD' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                                                }`}>
                                                    {refType}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <Typography className="text-sm font-medium text-gray-600 max-w-md truncate">
                                                    {entry.description || '-'}
                                                </Typography>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="text-xs font-mono text-gray-400">{entry.reference_id || '-'}</span>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <span className="text-sm font-black text-green-600">
                                                    +{formatCurrency(entry.amount)}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default IncomeReport;
